import { DragOverEvent, UniqueIdentifier } from '@dnd-kit/core';

import { BaseItem, GroupedItems } from '../../../../../../models/account';

const REMOVE_ID = 1_000_000_000;

export function arrayMove<T extends BaseItem>(
    groupedItems: GroupedItems<T>,
    fromId: string,
    fromIndex: number,
    toIndex: number,
    toGroupId: UniqueIdentifier,
): GroupedItems<T> {
    const fromGroup = groupedItems.find((group) => group.values.map((i) => i.id).includes(fromId));

    const item = fromGroup?.values.find((v) => v.id === fromId);

    const toGroup = groupedItems.find((group) => group.id === toGroupId);

    const newGrouping = groupedItems.reduce((acc, cur) => {
        if (fromGroup && toGroup && fromGroup.id === toGroup.id && cur.id === fromGroup.id) {
            const copiedValues = [...fromGroup.values.map((v) => ({ ...v }))];
            const from = { ...copiedValues[fromIndex] };
            copiedValues[fromIndex].id = REMOVE_ID;
            const filtered = copiedValues.filter((i) => i.id !== REMOVE_ID);
            const values = [...filtered.splice(0, toIndex), from, ...filtered.splice(0)];

            return [...acc, { name: fromGroup.name, id: fromGroup.id, values, isTemp: fromGroup.isTemp }];
        }

        if (fromGroup && cur.id === fromGroup.id) {
            return [
                ...acc,
                {
                    name: fromGroup.name,
                    id: fromGroup.id,
                    values: fromGroup.values.filter((v) => v.id !== fromId),
                    isTemp: fromGroup.isTemp,
                },
            ];
        }

        if (toGroup && item && cur.id === toGroup?.id) {
            return [
                ...acc,
                { name: toGroup.name, id: toGroup.id, values: [item, ...toGroup.values], isTemp: toGroup.isTemp },
            ];
        }

        return [...acc, cur];
    }, [] as GroupedItems<T>);

    return newGrouping;
}

export const isOverDifferentItem = (event: DragOverEvent) => {
    return event.active && event.over && event.active.data.current && event.over.data.current;
};

export const isInDifferentContext = (event: DragOverEvent) => {
    if (isOverDifferentItem(event)) {
        return (
            event.active &&
            event.over &&
            event.active.data.current &&
            event.over.data.current &&
            event.active.data.current.sortable.containerId !== event.over.data.current.sortable.containerId
        );
    }
    return event.active && event.over && event.active.id !== event.over.id;
};

export function deleteGroup<T extends BaseItem>(groupedItems: GroupedItems<T>, groupId: string): GroupedItems<T> {
    if (groupedItems.length === 1) {
        return groupedItems;
    }

    const groupIndex = groupedItems.findIndex((it) => it.id === groupId);

    if (groupIndex === -1) {
        return groupedItems;
    }

    const newIndex = groupIndex === groupedItems.length - 1 ? groupIndex - 1 : groupIndex;

    const result = [...groupedItems.filter((_, i) => i !== groupIndex)];

    result[newIndex] = {
        ...result[newIndex],
        values: [...result[newIndex].values, ...groupedItems[groupIndex].values],
    };

    return result;
}
